import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import { ArrowDownIcon, ArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Spinner } from '@communityshare/ui-shared';
import { HOME_VIDEO_AUDIENCES, isEligible } from '@/features/videos-common/audiences';
import { MAX_HOME_VIDEOS } from '../utils/homeSet';
import VideoThumbnail from './VideoThumbnail';
import StatusMessage from './StatusMessage';
import TextButton from './TextButton';

/**
 * The home-page sets, one tab per audience (Educator, Partner, Both). The
 * selected tab shows that audience's ordered set (slot 1 is the featured
 * card) with keyboard operable up/down reordering and unpin, the playlist
 * that drives its Next video, and warnings for pinned videos that are
 * unpublished or no longer tagged for the audience. Switching tabs changes
 * only this section.
 *
 * While a save is in flight the controls are aria-disabled rather than
 * disabled so keyboard focus stays on the button that was pressed. After the
 * list re-renders in its new order, focus is put back on the moved video's
 * button (or its opposite arrow when it reached the top or bottom).
 */
HomeVideos.propTypes = {
  audience: PropTypes.oneOf(HOME_VIDEO_AUDIENCES).isRequired,
  onAudienceChange: PropTypes.func.isRequired,
  videos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    thumbnailUrl: PropTypes.string,
    isPublished: PropTypes.bool,
    audiences: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }))
  })).isRequired,
  hasEligibleVideos: PropTypes.bool.isRequired,
  // undefined while playlists are unknown, null when the audience has none
  playlist: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string
  }),
  playlistsAnchorId: PropTypes.string,
  busy: PropTypes.bool,
  status: PropTypes.shape({
    tone: PropTypes.oneOf(['success', 'error']).isRequired,
    title: PropTypes.string.isRequired,
    detail: PropTypes.string
  }),
  fullMessageId: PropTypes.string.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  onUnpin: PropTypes.func.isRequired
};

export default function HomeVideos(props) {
  const {
    audience,
    onAudienceChange,
    videos,
    hasEligibleVideos,
    playlist,
    playlistsAnchorId,
    busy = false,
    status,
    fullMessageId,
    onMoveUp,
    onMoveDown,
    onUnpin
  } = props;
  const { t } = useTranslation();
  const audienceLabel = t(`staff_videos.audiences.${audience}`);

  const buttonRefs = useRef(new Map());
  const pendingFocus = useRef(null);
  const isFull = videos.length >= MAX_HOME_VIDEOS;

  function setRef(key) {
    return (node) => {
      if (node) buttonRefs.current.set(key, node);
      else buttonRefs.current.delete(key);
    };
  }

  useEffect(() => {
    const target = pendingFocus.current;
    if (!target || busy) return;
    pendingFocus.current = null;

    const preferred = buttonRefs.current.get(`${target.id}-${target.direction}`);
    const fallback = buttonRefs.current.get(`${target.id}-${target.direction === 'up' ? 'down' : 'up'}`);
    const button = preferred && !preferred.disabled ? preferred : fallback;
    if (button && document.activeElement !== button) button.focus();
  }, [videos, busy]);

  function guarded(handler) {
    return (...args) => {
      if (busy) return;
      handler(...args);
    };
  }

  const moveUp = guarded((video, index) => {
    pendingFocus.current = { id: video.id, direction: 'up' };
    onMoveUp(index);
  });

  const moveDown = guarded((video, index) => {
    pendingFocus.current = { id: video.id, direction: 'down' };
    onMoveDown(index);
  });

  const unpin = guarded((video) => onUnpin(video));

  function goToPlaylists(event) {
    const target = playlistsAnchorId && document.getElementById(playlistsAnchorId);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  }

  let playlistInfo = null;
  if (playlist) {
    playlistInfo = (
      <p className="text-sm text-cs-gray-700" data-testid="staff-home-playlist">
        {t('staff_videos.home.playlist', { title: playlist.title })}
      </p>
    );
  } else if (playlist === null) {
    playlistInfo = (
      <StatusMessage tone="warning" title={t('staff_videos.home.no_playlist')}>
        {playlistsAnchorId && (
          <a
            href={`#${playlistsAnchorId}`}
            onClick={goToPlaylists}
            className="text-sm font-semibold text-cs-blue-700 underline hover:text-cs-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
          >
            {t('staff_videos.home.no_playlist_link')}
          </a>
        )}
      </StatusMessage>
    );
  }

  const panel = (
    <div className="space-y-3">
      {playlistInfo}

      {!hasEligibleVideos && (
        <StatusMessage
          tone="warning"
          title={t('staff_videos.home.no_eligible')}
          detail={t(`staff_videos.home.no_eligible_${audience}`)}
        />
      )}

      {videos.length === 0 && hasEligibleVideos && (
        <p className="text-sm text-cs-gray-700 bg-white border border-cs-gray-200 rounded-cs-10 p-4">
          {t(`staff_videos.home.empty_${audience}`)} {t('staff_videos.home.pin_hint')}
        </p>
      )}

      {videos.length > 0 && (
        <ol className="bg-white border border-cs-gray-200 rounded-cs-10 divide-y divide-cs-gray-100" data-testid="staff-home-videos">
          {videos.map((video, index) => (
            <li key={video.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className={classNames(
                  'flex-shrink-0 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold min-w-[4.5rem]',
                  index === 0 ? 'bg-cs-orange-100 text-cs-orange-700' : 'bg-cs-gray-100 text-cs-gray-700'
                )}>
                  {index === 0 ? t('staff_videos.home.featured') : t('staff_videos.home.slot', { position: index + 1 })}
                </span>
                <VideoThumbnail url={video.thumbnailUrl} alt="" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-cs-gray-900">{video.title}</p>
                  {!video.isPublished && (
                    <Notice>{t('staff_videos.home.unpublished_notice')}</Notice>
                  )}
                  {!isEligible(video, audience) && (
                    <Notice>{t('staff_videos.home.ineligible_notice')}</Notice>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                <TextButton
                  ref={setRef(`${video.id}-up`)}
                  label={t('staff_videos.home.move_up')}
                  icon={ArrowUpIcon}
                  aria-label={t('staff_videos.home.move_up_label', { title: video.title })}
                  aria-disabled={busy || undefined}
                  disabled={index === 0}
                  onClick={() => moveUp(video, index)}
                />
                <TextButton
                  ref={setRef(`${video.id}-down`)}
                  label={t('staff_videos.home.move_down')}
                  icon={ArrowDownIcon}
                  aria-label={t('staff_videos.home.move_down_label', { title: video.title })}
                  aria-disabled={busy || undefined}
                  disabled={index === videos.length - 1}
                  onClick={() => moveDown(video, index)}
                />
                <TextButton
                  label={t('staff_videos.home.unpin')}
                  icon={XMarkIcon}
                  aria-label={t('staff_videos.home.unpin_label', { title: video.title, audience: audienceLabel })}
                  aria-disabled={busy || undefined}
                  onClick={() => unpin(video)}
                />
              </div>
            </li>
          ))}
        </ol>
      )}

      {isFull && (
        <p id={fullMessageId} className="text-sm text-cs-gray-700">
          {t('staff_videos.home.full', { audience: audienceLabel, max: MAX_HOME_VIDEOS })}
        </p>
      )}
    </div>
  );

  return (
    <section aria-labelledby="staff-home-videos-heading" className="space-y-3">
      <div className="flex flex-col space-y-1">
        <h2 id="staff-home-videos-heading" className="text-xl font-semibold text-cs-gray-900">
          {t('staff_videos.home.heading')}
        </h2>
        <p className="text-sm text-cs-gray-500">
          {t('staff_videos.home.description', { max: MAX_HOME_VIDEOS })}
        </p>
      </div>

      <Tab.Group
        selectedIndex={Math.max(0, HOME_VIDEO_AUDIENCES.indexOf(audience))}
        onChange={(index) => onAudienceChange(HOME_VIDEO_AUDIENCES[index])}
      >
        <Tab.List
          aria-label={t('staff_videos.home.tabs_label')}
          className="flex gap-1 border-b border-cs-gray-200"
        >
          {HOME_VIDEO_AUDIENCES.map((option) => (
            <Tab key={option} as={Fragment}>
              {({ selected }) => (
                <button
                  type="button"
                  className={classNames(
                    '-mb-px px-4 py-2 text-sm font-semibold border-b-2 rounded-t-cs-6',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-black',
                    selected
                      ? 'border-cs-orange-500 text-cs-gray-900'
                      : 'border-transparent text-cs-gray-500 hover:text-cs-gray-900 hover:border-cs-gray-300'
                  )}
                >
                  {t(`staff_videos.audiences.${option}`)}
                </button>
              )}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="pt-4">
          {HOME_VIDEO_AUDIENCES.map((option) => (
            <Tab.Panel key={option} className="focus:outline-none focus-visible:ring-2 focus-visible:ring-black rounded-cs-10">
              {option === audience ? panel : null}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>

      <div aria-live="polite">
        {busy && (
          <p className="flex items-center gap-2 text-sm text-cs-gray-700">
            <Spinner />
            {t('staff_videos.home.saving')}
          </p>
        )}
        {!busy && status && (
          <StatusMessage tone={status.tone} title={status.title} detail={status.detail} />
        )}
      </div>
    </section>
  );
}

function Notice({ children }) {
  return (
    <p className="mt-1 text-sm text-cs-gray-700 flex items-start gap-1">
      <span className="inline-block mt-1.5 h-2 w-2 rounded-full bg-cs-yellow-500 flex-shrink-0" aria-hidden="true" />
      {children}
    </p>
  );
}

Notice.propTypes = {
  children: PropTypes.node.isRequired
};
